import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, TextField, Typography, Stack,
} from '@mui/material';
import { displayPath, isSensitivePath } from './ivrExtensionFieldSchemas.js';

/**
 * מחיקת שלוחה עם אישור נתיב + אזהרת קבצים
 */
export default function IvrDeleteConfirmDialog({
  open,
  path = '',
  title = '',
  childCount = 0,
  fileCount = 0,
  deleting = false,
  onClose,
  onConfirm,
}) {
  const [typed, setTyped] = useState('');
  const [ackFiles, setAckFiles] = useState(false);
  const [error, setError] = useState('');

  const expected = String(path || '').replace(/^\/+/, '');
  const shown = displayPath(path);
  const blocked = childCount > 0;
  const sensitive = isSensitivePath(path);

  const canSubmit = useMemo(() => {
    if (blocked || deleting) return false;
    const typedNorm = typed.trim().replace(/^\/+/, '');
    if (typedNorm !== expected) return false;
    if (fileCount > 0 && !ackFiles) return false;
    return true;
  }, [blocked, deleting, typed, expected, fileCount, ackFiles]);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setAckFiles(false);
    setError('');
  }, [open, path]);

  const handleConfirm = () => {
    if (blocked) {
      setError('יש תת-שלוחות — מחקו אותן קודם');
      return;
    }
    if (!canSubmit) {
      setError('אשרו את כל התנאים לפני מחיקה');
      return;
    }
    onConfirm?.({
      path,
      confirmPath: expected,
      acknowledgeFiles: fileCount > 0 ? true : false,
    });
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: 'error.dark' }}>
        מחיקת שלוחה {shown}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {title && (
            <Typography variant="body2" color="text.secondary">
              כותרת: {title}
            </Typography>
          )}

          {blocked ? (
            <Alert severity="error">
              לא ניתן למחוק — יש {childCount} תת-שלוחות בתוך {shown}.
              {' '}
              היכנסו פנימה ומחקו קודם את תת-השלוחות, ואז חזרו לכאן.
            </Alert>
          ) : (
            <>
              <Alert severity="warning">
                לפני המחיקה יישמר
                {' '}
                <strong>גיבוי של הגדרות השלוחה</strong>
                {' '}
                (קובץ ההגדרות). אפשר לשחזר את ההגדרות אחר כך ממסך הגיבויים.
              </Alert>

              <Alert severity="info">
                קבצי שמע ותוכן אחר בתוך השלוחה
                {' '}
                <strong>יימחקו ולא ישוחזרו אוטומטית</strong>
                .
                הגיבוי שומר את ההגדרות בלבד.
              </Alert>

              {fileCount > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ackFiles}
                      onChange={(e) => setAckFiles(e.target.checked)}
                      color="error"
                    />
                  }
                  label={`אני מבין/ה ש-${fileCount} קבצים בשלוחה יימחקו לצמיתות`}
                />
              )}

              {sensitive && (
                <Alert severity="warning">
                  זה נתיב חשוב במערכת הבחינות. המחיקה תשפיע על מתקשרים.
                </Alert>
              )}

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  לאישור — הקלידו את הנתיב המדויק:
                  {' '}
                  <Box component="span" sx={{ fontWeight: 800, direction: 'ltr', display: 'inline-block' }}>
                    {shown}
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={shown}
                  inputProps={{ dir: 'ltr' }}
                  disabled={deleting}
                />
              </Box>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={deleting}>ביטול</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!canSubmit}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          מחק עם גיבוי
        </Button>
      </DialogActions>
    </Dialog>
  );
}
